/**
 * Warm-hearth personal theme attribute.
 *
 * Stamps `data-account-type="personal"` on <html> whenever the current
 * organization is a PERSONAL space, and removes it otherwise. The token
 * overrides in styles/colors.css key off this attribute — with it absent,
 * work orgs render byte-identical to before.
 *
 * Seam design (mirrors how light/dark + data-sidebar-theme survive):
 * - The org store is the live source of truth: a module-level subscription
 *   restamps the attribute on every org switch (personal → work removes it).
 * - Desktop popup/companion windows boot their own JS context where the org
 *   store starts empty and fills in async (usePopupOrganizationInit). To avoid
 *   a cold-boot flash of the wrong theme, the resolved account type is cached
 *   in localStorage (shared across windows) and applied synchronously at boot,
 *   exactly like the `scrambl-ui-theme` light/dark cache.
 * - A `storage` listener keeps already-open windows in sync when the main
 *   window switches orgs.
 *
 * Client-portal routes are excluded: portals belong to host (work) orgs and
 * deliberately never initialize the personal-space org context.
 */

import { AccountType } from '@scrambl/shared'

import { useOrganizationStore } from '@/store/organization.store'

const STORAGE_KEY = 'scrambl-account-type'
const ATTRIBUTE = 'data-account-type'
const PERSONAL = 'personal'

function isClientPortalWindow(): boolean {
  return window.location.pathname.startsWith('/client-portal')
}

function applyAccountType(accountType: string | null | undefined): void {
  const root = document.documentElement
  if (accountType === AccountType.PERSONAL) {
    root.setAttribute(ATTRIBUTE, PERSONAL)
  } else {
    root.removeAttribute(ATTRIBUTE)
  }
}

/**
 * Authoritatively re-derive the theme from a resolved organization.
 *
 * Resolution rule (the single source of truth for the warmth):
 *   attribute present  IFF  the resolved org's accountType === PERSONAL.
 *   Anything else — a business org, an org whose accountType is missing/null
 *   (legacy rows, minimal/partial org objects), or an external host org —
 *   removes the attribute. The resolved value is written straight back to the
 *   cross-window cache so it never goes stale (a work org overwrites a leftover
 *   'PERSONAL', and popup windows following via the `storage` event get the
 *   corrected value).
 *
 * Pass `null` for "no organisation" (logout / store reset): that both drops the
 * warmth and clears the cache so the next cold boot paints neutral.
 *
 * The store subscription fires on store *transitions*, which a window does not
 * always see — it can boot straight into the target org from the synchronous
 * cache, or be primed from a stale `currentOrganizationId` / a partial org
 * object that never carried the personal org through this window. Callers that
 * authoritatively know "this is the org for the current route" (OrgRouteGuard)
 * therefore call this directly so the theme tracks the org actually being
 * viewed rather than whatever the boot cache last held.
 */
export function syncAccountTypeTheme(
  accountType: string | null | undefined,
  hasOrg = true,
): void {
  if (typeof window === 'undefined' || isClientPortalWindow()) return

  if (!hasOrg) {
    applyAccountType(null)
    localStorage.removeItem(STORAGE_KEY)
    return
  }

  const resolved = accountType ?? AccountType.ORGANIZATION
  applyAccountType(resolved)
  localStorage.setItem(STORAGE_KEY, resolved)
}

let initialized = false

/**
 * Call once per window, before first render (main.tsx). Idempotent.
 */
export function initPersonalThemeAttribute(): void {
  if (initialized || typeof window === 'undefined') return
  initialized = true

  if (isClientPortalWindow()) return

  // Synchronous boot stamp from the cross-window cache, so popup windows and
  // reloads paint warm immediately instead of flashing the work theme while
  // the org store hydrates.
  applyAccountType(localStorage.getItem(STORAGE_KEY))

  // Live source of truth: restamp on every org change in this window. Delegates
  // to the shared resolver so the store path and the route-level re-stamp
  // (OrgRouteGuard) can never disagree.
  useOrganizationStore.subscribe((state, prevState) => {
    const org = state.currentOrganization
    if (org === prevState.currentOrganization) return

    // Logout / store reset drops the warmth and the cache; otherwise re-derive
    // from the (possibly partial) org object.
    syncAccountTypeTheme(org?.accountType, !!org)
  })

  // Cross-window sync: popup windows follow the main window's org switches
  // (all windows track the same `currentOrganizationId`, so they track the
  // same account type).
  window.addEventListener('storage', event => {
    if (event.key === STORAGE_KEY) {
      applyAccountType(event.newValue)
    }
  })
}
